import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useImperativeHandle,
} from 'react';
import { Redirect, useHistory, useLocation } from 'react-router-dom';
import { featureEnabled, isUserMlsStaff } from 'utils';
import styled from 'styled-components';
import { Input } from 'antd';
import { useBreakpoint, useDebounce, usePrevious } from 'hooks';
import {
  AddListingToTourModalContext,
  AuthContext,
  ExternalAuthContext,
  ContactDrawerContext,
  ContactDrawerMode,
  ContactDrawerType,
  LayoutContext,
  UniversalSearchContext,
  CrossOrgAnsweringContext,
  UserFavoritesContext,
  ListingLabelContext,
  ContextualChatContext,
} from 'contexts';
import errorReporter from '@app/errorReporter';
import { UserRoles } from '@bb/users';
import type { AnsweringSubscriptionTag } from '@bb/domain/dist/types';

import { TourItemContent } from 'api/TourController/types';
import { Color, Icon as NewIcon } from '@bb/elements-web';
import Button from '../Button';
import Icon from '../Icon';
import message from '../Message';
import Spin from '../Spin';
import Tabs, { TabPane } from '../Tabs';
import theme from '../theme';
import {
  getAgentSearchAppointmentDeskCrossOrg,
  getAgentSearchCrossOrg,
  mapAgentResultsToTable,
} from './agentSearch';
import { appointmentDeskCrossOrgListingSearch } from './answeringListingSearch';
import getListingSearchCrossOrg, {
  mapListingResultsToTable,
} from './listingSearch';
import externalListingSearch from './externalListingSearch';
import { crossOrgAppointmentDeskOrgSearch } from './orgSearch';
import {
  BrokerageFilterType,
  FilteredMlsOptionsType,
  OrgType,
  SearchOptions,
  SearchTypeKey,
  UniversalSearchState,
} from './types';
import {
  StyledClearRecentsButton,
  StyledBrokerageFilter,
  StyledRecentsHeading,
  StyledSearchResults,
  StyledTable,
  StyledUniversalSearch,
} from './StyledComponents';
import { Select, SelectOption } from '../Select';
import {
  getHistory,
  clearHistory,
} from '../../api/RecentSearchHistoryController';
import { getMlsTypeFilterOptions } from '../../api/AnsweringOrgController';
import getStopSearch from './stopSearch';
import MlsFilterDropdown from './MlsFilterDropdown';
import { MLS_TYPE_LABEL_MAP } from './constants';

const getPlaceholderTextMap = ({
  isAssumed,
  external,
  showingsFeatureOn,
}: {
  isAssumed?: boolean;
  external?: boolean;
  showingsFeatureOn?: boolean;
}): Record<OrgType, string> =>
  showingsFeatureOn
    ? {
        brokerage: `Search listings${external ? '' : ' or agents'}...`,
        answering: `Search listings, brokerages${
          isAssumed ? '' : ', agents'
        }...`,
      }
    : {
        brokerage: `Search ${external ? 'Address' : 'Address or Agent'}`,
        answering: `Search Address, Brokerage${isAssumed ? '' : ', Agent'}`,
      };

export const ANSWERING_DROPDOWN_WIDTH = 1072;
export const BROKERAGE_DROPDOWN_WIDTH = 900;

export type UniversalSearchProps = {
  orgType: OrgType;
  external?: boolean;
  className?: string;
  showTable?: boolean;
  searchFromTour?: boolean;
  customOffset?: number;
  customPlaceholder?: string;
  openTourListingModal?: (listingId: string) => void;
  addListingToTour?: (listingId: string) => void;
  addStopToTour?: (
    stop: Pick<TourItemContent, 'address' | 'locationName' | 'coordinates'>,
  ) => Promise<void>;
  searchIsDisabled?: boolean;
  onSearchClose?: () => void;
};

export const UniversalSearch: React.ForwardRefRenderFunction<
  {
    inputRef: React.MutableRefObject<any>;
  },
  UniversalSearchProps
> = (
  {
    orgType,
    external,
    className = '',
    showTable = false,
    searchFromTour = false,
    customOffset,
    customPlaceholder,
    openTourListingModal,
    addListingToTour,
    addStopToTour,
    searchIsDisabled = false,
    onSearchClose,
  },
  ref,
) => {
  const history = useHistory();
  const location = useLocation();
  const { md, lg, xl } = useBreakpoint();
  const memoizedFalse = useMemo(() => () => false, []);
  const { auth, hasFeature = memoizedFalse } = useContext(AuthContext);
  const { auth: externalAuth } = React.useContext(ExternalAuthContext);

  const { getBoardName } = useContext(ListingLabelContext);
  const { chatModalVisible } = useContext(ContextualChatContext);
  const { openContactDrawer } = useContext(ContactDrawerContext);
  const { offsetTop, getSiderCurrentWidth } = useContext(LayoutContext);
  const {
    activeRow,
    setActiveRow,
    searchOpenFromTour,
    setSearchOpenFromTour,
    closeSearchFromTourModal,
    setCloseSearchFromTourModal,
    universalSearchRef: inputRef,
    setSearchClose,
  } = useContext(UniversalSearchContext);
  const { answeringConfirm, answeringConfirming } =
    useContext(CrossOrgAnsweringContext) || {};
  const { listing: listingToAddToTour } =
    useContext(AddListingToTourModalContext) || {};
  const { favorites, toggleFavorite } = useContext(UserFavoritesContext) || {};
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [state, setState] = useState<UniversalSearchState>({
    loading: false,
    redirect: '',
  });
  const [tabKey, setTabKey] = useState<SearchTypeKey>('listing');
  const [brokerageFilter, setBrokerageFilter] =
    useState<BrokerageFilterType>(null);
  const [listingHistory, setListingHistory] = useState([]);
  const [agentHistory, setAgentHistory] = useState([]);

  // Start: Functions for MLS Filter for Answering Service
  const [mlsOptions, setMlsOptions] = useState([]);
  const [filteredMlsOptions, setFilteredMlsOptions] = useState({});
  const [mlsFilterVisible, setMlsFilterVisible] = useState(false);

  const showingsFeatureOn = useMemo(
    () =>
      featureEnabled('showingsMadeEasyFeature') &&
      !external &&
      auth?.org.type === 'brokerage',
    [auth, external],
  );

  const resetFilteredMlsOptions = useCallback(
    (options: { label: string; value: string }[]) => {
      if (orgType === 'answering' && mlsOptions) {
        const updated = options.reduce((acc: any, { value }) => {
          acc[value] = false;
          return acc;
        }, {});
        setFilteredMlsOptions(updated);
      }
    },
    [mlsOptions, orgType],
  );

  useEffect(() => {
    if (orgType === 'answering' && !isUserMlsStaff(auth)) {
      const buildMlsOptions = (options: string[]) => {
        const databaseOptions = options.reduce((acc, curr) => {
          acc.push({
            label: MLS_TYPE_LABEL_MAP[curr] || curr,
            value: curr,
          });
          return acc;
        }, []);
        return [...databaseOptions];
      };

      const fetchOptions = async () => {
        const options = await getMlsTypeFilterOptions();
        setMlsOptions(buildMlsOptions(options));
      };

      fetchOptions();
    }
  }, [orgType, auth]);

  useEffect(() => {
    resetFilteredMlsOptions(mlsOptions);
  }, [resetFilteredMlsOptions, mlsOptions]);
  // End: Functions for MLS Filter for Answering Service

  const dataObject = useMemo(
    () => ({
      listing: external
        ? externalListingSearch({ lg, showChatBtn: false }, setActiveRow)
        : orgType === 'brokerage'
          ? getListingSearchCrossOrg(
              {
                lg,
                showChatBtn: hasFeature('CHAT'),
              },
              setActiveRow,
            )
          : appointmentDeskCrossOrgListingSearch(
              {
                lg,
                showChatBtn: hasFeature('CHAT'),
              },
              setActiveRow,
            ),
      org: crossOrgAppointmentDeskOrgSearch(
        isUserMlsStaff(auth),
        setActiveRow,
        answeringConfirm,
      ),
      agent:
        orgType === 'brokerage'
          ? getAgentSearchCrossOrg(
              {
                lg,
                showChatBtn: hasFeature('CHAT'),
              },
              setActiveRow,
              getBoardName,
            )
          : getAgentSearchAppointmentDeskCrossOrg(
              { auth },
              setActiveRow,
              getBoardName,
            ),
      stop: getStopSearch(setActiveRow),
    }),
    [hasFeature, lg],
  );
  const { columns, search, onConfirm, createOnRow } = dataObject[tabKey];
  const currentTabKey = useRef(tabKey);
  const previousTabKey = usePrevious(tabKey);
  const updateState = (changes: object) => {
    setState((oldState: UniversalSearchState) => {
      const newState = { ...oldState, ...changes };
      return newState;
    });
  };

  const debouncedQuery = useDebounce(query, 500);

  const wrapperRef = useRef(null);
  const searchBarRef = useRef(null);
  const clearSearchRef = useRef(null);

  useImperativeHandle(ref, () => ({ inputRef }), []);

  const forceBlur = useCallback(() => {
    setActiveRow(null);
    setIsInputFocused(false);
    setQuery('');
    setItems([]);
    setTabKey('listing');
    resetFilteredMlsOptions(mlsOptions);
    setSearchClose();
    return inputRef?.current?.blur();
  }, [mlsOptions]);

  // Closed from tours page
  useEffect(() => {
    if (searchFromTour && !searchOpenFromTour) {
      forceBlur();
    }
  }, [searchFromTour, searchOpenFromTour, forceBlur]);

  useEffect(() => {
    if (!isInputFocused) {
      onSearchClose?.();
    }
  }, [isInputFocused]);

  useEffect(() => {
    if (closeSearchFromTourModal) {
      setCloseSearchFromTourModal(false);
      forceBlur();
    }
  }, [closeSearchFromTourModal, setCloseSearchFromTourModal, forceBlur]);

  const moveSelection = useCallback(
    (value: number) => {
      const lastIndex = items.length - 1;

      if (activeRow === null) {
        if (value === -1) return setActiveRow(lastIndex);
        return setActiveRow(0);
      }

      const newValue = activeRow + value;
      if (newValue > lastIndex) return setActiveRow(0);
      if (newValue < 0) return setActiveRow(lastIndex);
      return setActiveRow(newValue);
    },
    [items, activeRow, setActiveRow],
  );
  // scrolling when using keyboard
  useEffect(() => {
    const row = document.querySelector('tr.active');
    const offset = -80;
    if (row) {
      const { top } = row.getBoundingClientRect();
      const inViewPort =
        top + offset >= 0 && top - offset <= window.innerHeight;
      if (!inViewPort) {
        row.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'center',
        });
      }
    }
  }, [activeRow]);

  const openAgentDrawer = useCallback(
    (agentId: string, subscriptionTag?: AnsweringSubscriptionTag) => {
      openContactDrawer(
        ContactDrawerMode.Profile,
        ContactDrawerType.User,
        agentId,
        { subscriptionTag },
      );
    },
    [openContactDrawer],
  );

  const confirmSelection = useCallback(() => {
    const isViewing = !auth?.isAdmin; // default to view as an agent
    if (activeRow === null) return;
    onConfirm(items[activeRow], {
      updateState,
      isViewing,
      auth,
      openAgentDrawer,
      forceBlur,
      history,
      location,
      addStopToTour,
    });
    forceBlur();
  }, [
    auth,
    forceBlur,
    items,
    onConfirm,
    openAgentDrawer,
    state,
    location,
    addStopToTour,
    activeRow,
  ]);

  const setKeys = useCallback(
    (event: KeyboardEvent) => {
      event.stopPropagation();
      const { shiftKey, key } = event;
      if (!isInputFocused) {
        if (key === 'Enter' && shiftKey && !chatModalVisible) {
          // isAnyOpenModal check works for all antd modals, instead of checking for each modal's specific state,
          // we can check for the common class 'ant-modal-mask' which is added to the body when any antd modal is open
          // if any open modal with class 'ant-modal-mask' exists,
          // do not focus on universal search
          const isAnyOpenModal = document.querySelector('.ant-modal-mask');
          return !isAnyOpenModal && inputRef?.current?.focus();
        }
        return null;
      }
      if (isInputFocused) {
        if (key === 'Tab' && shiftKey) forceBlur();
      }
      if (!items) return null;

      switch (key) {
        case 'Escape':
          return forceBlur();
        case 'ArrowDown':
          return moveSelection(1);
        case 'ArrowUp':
          return moveSelection(-1);
        case 'Enter':
          return confirmSelection();
        default:
          break;
      }
    },
    [confirmSelection, forceBlur, moveSelection, isInputFocused, items],
  );

  const setMouseClicks = useCallback(
    ({ target }: MouseEvent) => {
      if (
        isInputFocused &&
        !wrapperRef.current?.contains(target) &&
        !clearSearchRef.current?.contains(target) &&
        inputRef.current.input !== target && // inputRef.current is `Antd Input component instance`
        !searchBarRef.current.contains(target) &&
        !listingToAddToTour && // prevents search close if AddListingToTourModal is closed
        !answeringConfirming
      ) {
        forceBlur();
      } else if (!isInputFocused && searchBarRef.current.contains(target)) {
        inputRef?.current?.focus();
      }
    },
    [
      forceBlur,
      isInputFocused,
      orgType,
      listingToAddToTour,
      answeringConfirming,
    ],
  );

  useEffect(() => {
    const fetchHistory = async () => {
      const data = await getHistory();
      if (data) {
        const { listings, agents } = data;
        setListingHistory(
          listings.map((listing: any) =>
            mapListingResultsToTable(listing, forceBlur, listings.length, {
              auth,
              favorites,
              toggleFavorite,
              lg,
            } as SearchOptions),
          ),
        );
        setAgentHistory(agents.map(mapAgentResultsToTable(auth)));
      }
    };
    if (!searchFromTour && isInputFocused && !debouncedQuery && auth)
      fetchHistory();
  }, [toggleFavorite, !debouncedQuery, searchFromTour, isInputFocused, auth]);

  useEffect(() => {
    let defaultValue: BrokerageFilterType = auth?.org?.mlsTypes?.length
      ? 'allBrokerages'
      : 'myBrokerage';
    if (
      auth?.role === UserRoles.Agent ||
      auth?.role === UserRoles['Team Admin'] ||
      searchFromTour
    )
      defaultValue = 'allBrokerages';
    setBrokerageFilter(defaultValue);
  }, [auth?.role, isInputFocused, searchFromTour]);

  useEffect(() => {
    currentTabKey.current = tabKey;
  }, [tabKey]);

  useEffect(() => {
    document.addEventListener('keydown', setKeys);
    document.addEventListener('mouseup', setMouseClicks);

    return () => {
      document.removeEventListener('keydown', setKeys);
      document.removeEventListener('mouseup', setMouseClicks);
    };
  }, [setKeys, setMouseClicks]);

  useEffect(() => {
    const runSearch = async () => {
      if (!debouncedQuery) {
        updateState({ loading: false });
        setItems([]);
        return;
      }
      try {
        updateState({ loading: true });
        const mlsFilter = Object.entries(filteredMlsOptions)
          .filter(([, value]) => value)
          .map(([key]) => key);
        const searchedItems = await search(debouncedQuery, forceBlur, {
          auth,
          brokerageFilter,
          dashboardId: externalAuth?.dashboardId,
          retsId: externalAuth?.retsId,
          favorites,
          toggleFavorite,
          searchFromTour,
          openTourListingModal,
          mlsOptionsCount: mlsOptions.length,
          mlsFilter: mlsFilter.length ? mlsFilter : undefined,
        });
        // this condition prevents the app from crashing in case the user
        // changes the tab in middle of a network request
        if (currentTabKey.current !== tabKey) {
          return;
        }
        updateState({ loading: false });
        setItems(searchedItems);
      } catch (err) {
        updateState({ loading: false });
        errorReporter.error(err);
        message.error('Failed to load data');
      }
    };
    if ((previousTabKey !== tabKey || !state.loading) && isInputFocused) {
      runSearch();
    }
  }, [
    agentHistory,
    auth,
    brokerageFilter,
    debouncedQuery,
    favorites,
    filteredMlsOptions,
    forceBlur,
    isInputFocused,
    lg,
    listingHistory,
    mlsOptions,
    orgType,
    openTourListingModal,
    tabKey,
    toggleFavorite,
    search,
    searchFromTour,
  ]);

  useEffect(() => {
    if (!md || state.redirect) forceBlur();
  }, [md, state.redirect, forceBlur]);

  const description = useMemo(() => {
    if (tabKey === 'listing') return 'Search for an address or enter an MLS #';
    if (tabKey === 'org') return 'Search for a brokerage';
    if (tabKey === 'agent') return 'Search for an agent';
    if (tabKey === 'stop')
      return 'Search for anything on Google Maps like ‘Starbucks’';
    if (auth?.isAssumedNotBB)
      return 'Type and select either Listings or Brokerages to search';
    return 'Type and select either Listings, Brokerages or Agents to search';
  }, [orgType, tabKey, auth?.isAssumedNotBB]);

  const getEmptyStateDescription = useMemo(() => {
    const hasLinkAccountPhase2Feature = featureEnabled(
      'linkAccountPhaseFeature',
      2,
    );
    const { linkedAccounts, org: { extendedMlsTypes = [] } = {} } = auth || {};
    let descriptionText: [string, string | JSX.Element] = ['', ''];
    const listingDescriptionText: [string, string | JSX.Element] =
      hasLinkAccountPhase2Feature
        ? [
            `We could not find the listing in ${extendedMlsTypes.join(', ')} database.`,
            linkedAccounts?.length > 1 ? (
              <b>
                Try switching to another account next to the search field and
                search again.
              </b>
            ) : (
              'Please enter a valid address or MLS #'
            ),
          ]
        : [
            'We could not find the listing.',
            'Please enter a valid address or MLS #',
          ];

    if (tabKey === 'listing') descriptionText = listingDescriptionText;
    else if (tabKey === 'org')
      descriptionText = [
        'We could not find the brokerage.',
        'Please enter a valid name',
      ];
    else if (tabKey === 'agent')
      descriptionText = [
        'We could not find the agent.',
        'Please enter a valid name',
      ];
    else if (tabKey === 'stop')
      descriptionText = [
        `Google Maps can't find '${debouncedQuery}'`,
        'Make sure your search is spelled correctly. Try adding a city, state, or zip code.',
      ];

    return (
      <StyledDefaultDescription data-cy={`${tabKey}-placeholder`}>
        {descriptionText[0]}
        <br />
        {descriptionText[1]}
      </StyledDefaultDescription>
    );
  }, [orgType, tabKey, debouncedQuery]);

  const descriptionComponent = useMemo(
    () => (
      <StyledDefaultDescription data-cy={`${tabKey}-placeholder`}>
        {description}
      </StyledDefaultDescription>
    ),
    [description, tabKey],
  );

  const hideDescription =
    (orgType === 'brokerage' &&
      tabKey === 'listing' &&
      listingHistory.length) ||
    (orgType === 'brokerage' && tabKey === 'agent' && agentHistory.length) ||
    debouncedQuery;

  const tableToRender = useMemo(() => {
    const getTable = (dataSource: any, offset?: number) => (
      <StyledTable
        lg={lg}
        md={md}
        size="small"
        dataSource={dataSource}
        columns={columns}
        pagination={false}
        searchFromTour={searchFromTour}
        customOffset={offset}
        hasShowingsMadeEasyFeature={showingsFeatureOn}
        className={`${tabKey}${orgType === 'brokerage' ? ' cross-org' : ''}`}
        locale={{
          emptyText: getEmptyStateDescription,
        }}
        rowClassName={(record: any, i: number) =>
          activeRow === i ? 'active' : ''
        }
        rowKey={(record: any) => record.id}
        showHeader={false}
        onRow={
          createOnRow &&
          createOnRow({
            auth,
            openAgentDrawer,
            forceBlur,
            history,
            location,
            addListingToTour,
            addStopToTour,
            answeringConfirm,
          })
        }
        data-cy={`universal-search-${
          tabKey === 'org' ? 'brokerage' : tabKey
        }-table`}
      />
    );

    if (state.loading || !hideDescription) {
      return descriptionComponent;
    }

    if (
      !debouncedQuery &&
      !!listingHistory.length &&
      !searchFromTour &&
      tabKey === 'listing'
    ) {
      return (
        <StyledSearchHistoryContainer>
          <StyledRecentsHeading type="secondary">
            Recently viewed listings
          </StyledRecentsHeading>
          {getTable(listingHistory, 50)}
        </StyledSearchHistoryContainer>
      );
    }

    if (!debouncedQuery && !!agentHistory.length && tabKey === 'agent') {
      return (
        <StyledSearchHistoryContainer>
          <StyledRecentsHeading type="secondary">
            Recently viewed agents
          </StyledRecentsHeading>
          {getTable(agentHistory, 50)}
        </StyledSearchHistoryContainer>
      );
    }

    if (searchFromTour) return getTable(items, customOffset);
    return getTable(items);
  }, [
    agentHistory,
    columns,
    createOnRow,
    debouncedQuery,
    descriptionComponent,
    forceBlur,
    getEmptyStateDescription,
    hideDescription,
    items,
    lg,
    listingHistory,
    openAgentDrawer,
    orgType,
    activeRow,
    tabKey,
    addListingToTour,
    customOffset,
    searchFromTour,
    history,
    state.loading,
  ]);

  const overlayContent = useMemo(() => {
    const showClearRecents =
      orgType === 'brokerage' &&
      !debouncedQuery &&
      !searchFromTour &&
      ((!!listingHistory.length && tabKey === 'listing') ||
        (!!agentHistory.length && tabKey === 'agent'));

    return (
      <span ref={wrapperRef}>
        <StyledTabs
          activeKey={tabKey}
          onChange={key => {
            if (key !== tabKey) setItems([]);
            setActiveRow(null);
            setTabKey(key as SearchTypeKey);
          }}
          $searchFromTour={searchFromTour}
          $md={md}
        >
          <TabPane
            tab={<span data-cy="listings-header">Listings</span>}
            key="listing"
          >
            {tableToRender}
          </TabPane>
          {!external &&
            /* eslint-disable-next-line no-nested-ternary */
            (searchFromTour ? (
              <TabPane tab="Stops" key="stop">
                {tableToRender}
              </TabPane>
            ) : orgType === 'brokerage' ? (
              <TabPane tab="Agents" key="agent">
                {tableToRender}
              </TabPane>
            ) : (
              <>
                <TabPane
                  tab={<span data-cy="brokerages-header">Brokerages</span>}
                  key="org"
                >
                  {tableToRender}
                </TabPane>
                {/* Agents tab should render for mls staff users when they are assuming but not for answering users */}
                {(!(auth?.isAssumedNotBB || auth?.assumerIdMlsStaff) ||
                  (auth?.isAssumedNotBB &&
                    auth?.parentOrgType === 'mlsStaff') ||
                  (auth?.assumerIdMlsStaff &&
                    auth?.parentOrgType === 'mlsStaff')) && (
                  <TabPane
                    tab={<span data-cy="agents-header">Agents</span>}
                    key="agent"
                  >
                    {tableToRender}
                  </TabPane>
                )}
              </>
            ))}
        </StyledTabs>
        {orgType === 'answering' &&
          !isUserMlsStaff(auth) &&
          !searchFromTour && (
            <MlsFilterDropdown
              mlsOptions={mlsOptions}
              filteredMlsOptions={filteredMlsOptions as FilteredMlsOptionsType}
              setFilteredMlsOptions={setFilteredMlsOptions}
              setVisible={setMlsFilterVisible}
              visible={mlsFilterVisible}
            />
          )}
        {showClearRecents && (
          <StyledClearRecentsButton
            className="clear-recents"
            type="link"
            onClick={() => {
              if (tabKey === 'listing') setListingHistory([]);
              if (tabKey === 'agent') setAgentHistory([]);
              clearHistory(tabKey);
            }}
          >
            Clear Recents
          </StyledClearRecentsButton>
        )}
      </span>
    );
  }, [
    agentHistory.length,
    auth?.isAssumedNotBB,
    auth?.parentOrgType,
    debouncedQuery,
    listingHistory.length,
    orgType,
    tabKey,
    tableToRender,
    searchFromTour,
    setActiveRow,
    md,
    mlsOptions,
    filteredMlsOptions,
    setFilteredMlsOptions,
    mlsFilterVisible,
  ]);

  const searchResultsComponent = useMemo(
    () => (
      <StyledSearchResults $searchFromTour={searchFromTour}>
        {overlayContent}
      </StyledSearchResults>
    ),
    [orgType, overlayContent],
  );

  return (
    <StyledUniversalSearch
      isInputFocused={isInputFocused}
      orgType={orgType}
      isAssumedNotBB={auth?.isAssumedNotBB}
      assumerIdMlsStaff={auth?.assumerIdMlsStaff}
      offsetTop={customOffset ?? offsetTop}
      xl={xl}
      siderWidth={getSiderCurrentWidth()}
      ref={searchBarRef}
      searchFromTour={searchFromTour}
      {...(searchFromTour && { id: 'tour-universal-search' })}
      data-cy="universal-search-bar"
      hasShowingsMadeEasyFeature={showingsFeatureOn}
    >
      {state.redirect && <Redirect to={state.redirect} />}
      <div className="top-row">
        <Input
          className={className}
          onFocus={() => {
            setIsInputFocused(true);
            if (searchFromTour) setSearchOpenFromTour(true);
          }}
          size="large"
          autoComplete="off"
          {...(searchFromTour && { 'data-cy': 'tours-search-listings-stops' })}
          name="universal-search"
          ref={inputRef}
          prefix={
            <div className="icon-wrapper">
              {state.loading ? (
                <Spin size="small" className="spin-icon" />
              ) : showingsFeatureOn ? (
                <NewIcon
                  icon="searchOutlined"
                  color={searchFromTour ? 'black' : 'primary'}
                  size={20}
                />
              ) : (
                <Icon
                  type="search"
                  height={20}
                  width={20}
                  {...(searchFromTour && { color: 'black' })}
                />
              )}
            </div>
          }
          suffix={
            <>
              {(query || (searchFromTour && isInputFocused)) && (
                <Button
                  ref={clearSearchRef}
                  onClick={() => {
                    setQuery('');
                    setActiveRow(null);
                  }}
                  type="text"
                  shape="circle"
                  icon={<Icon type="x" offset={[0, 2]} />}
                  data-cy="clear-search-button"
                />
              )}
              {searchFromTour && isInputFocused && (
                <StyledCancelButton
                  onClick={e => {
                    forceBlur();
                    // stop click event from bubbling up to the input element -> causing re-focus
                    e.stopPropagation();
                  }}
                  data-cy="cancel-search-button"
                >
                  Cancel
                </StyledCancelButton>
              )}
            </>
          }
          placeholder={
            customPlaceholder ??
            `${
              getPlaceholderTextMap({
                isAssumed: auth?.isAssumedNotBB,
                external,
                showingsFeatureOn,
              })[orgType]
            } (Shift + Enter)`
          }
          value={query}
          onChange={({ target: { value } }) => setQuery(value)}
          disabled={searchIsDisabled}
          {...(searchFromTour && !md
            ? {
                onPressEnter: () => inputRef.current.blur(),
                enterkeyhint: 'Done',
              }
            : {})}
        />
        {orgType === 'brokerage' && !!query && !searchFromTour && !external && (
          <StyledBrokerageFilter>
            <Select
              data-cy="select-brokerage-filter"
              value={brokerageFilter}
              bordered={false}
              onChange={value => setBrokerageFilter(value)}
              getPopupContainer={el => el.parentElement}
            >
              {auth?.org?.mlsTypes?.length && (
                <SelectOption
                  className="select-option"
                  key={'allBrokerages'}
                  value={'allBrokerages'}
                >
                  Across MLS(s)
                </SelectOption>
              )}
              <SelectOption
                className="select-option"
                key={'myBrokerage'}
                value={'myBrokerage'}
              >
                My Brokerage
              </SelectOption>
            </Select>
          </StyledBrokerageFilter>
        )}
      </div>
      {(showTable || isInputFocused) && searchResultsComponent}
    </StyledUniversalSearch>
  );
};

export default React.forwardRef<
  { inputRef: React.MutableRefObject<any> },
  UniversalSearchProps
>(UniversalSearch);

const StyledTabs = styled(Tabs)<{
  $md?: boolean;
  $searchFromTour?: boolean;
}>`
  .ant-tabs-nav {
    margin: 0;
    padding-left: 16px;
  }

  ${({ $md, $searchFromTour }) =>
    !$md &&
    $searchFromTour &&
    `.ant-tabs-content-holder {
      height: 100vh;
    }

    .ant-tabs-nav {
      padding: 0;
    }

    .ant-tabs-nav-wrap {
      display: flex;
      justify-content: center;

      .ant-tabs-nav-list div:nth-child(2) {
        margin: 0 0 0 32px;
      }
    }

    .ant-tabs-nav-list {
      gap: 5px
    }
    `}

  .ant-tabs-ink-bar {
    background-color: ${Color.PRIMARY};
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${Color.PRIMARY};
  }
`;

const StyledDefaultDescription = styled.div`
  padding: 48px 0;
  background-color: ${theme.white};
  color: ${theme.gray6};
  font-size: 16px;
  text-align: center;
`;

const StyledCancelButton = styled(Button)`
  margin-right: 10px;
  border: none;
  font-weight: 400;
`;

const StyledSearchHistoryContainer = styled.div`
  display: flex;
  flex-direction: column;
`;
